import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import AdminReviewManager from './AdminReviewManager';
import AdminPhotoApprovals from './AdminPhotoApprovals';
import AdminSponsoredManager from './AdminSponsoredManager';
import PromoCodeManager from './PromoCodeManager';
import BackupManager from './BackupManager';
import VerificationReviewCard from './VerificationReviewCard';
import ContactChangeRequestsModal from './ContactChangeRequestsModal';
import {
  LayoutDashboard, Users, UserPlus, Search, Stethoscope, Building2, PhoneCall,
  MessageSquareQuote, Sparkles, Ticket, TrendingUp, HardDriveDownload, UserCheck,
  Image as ImageIcon,
} from 'lucide-react';

const AdminPanel = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { user } = useUnifiedAuth();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(50); // 50 users per page
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [adminToken, setAdminToken] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [userAppointments, setUserAppointments] = useState([]);
  const [error, setError] = useState('');
  const [showDoctorVerification, setShowDoctorVerification] = useState(false);
  const [showProviderVerification, setShowProviderVerification] = useState(false);
  const [showContactChangeRequests, setShowContactChangeRequests] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [pendingProviders, setPendingProviders] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [sortBy, setSortBy] = useState('date'); // 'date', 'type', 'tier', 'name'
  const [activeNav, setActiveNav] = useState('overview');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [tierModalUser, setTierModalUser] = useState(null); // For tier duration modal
  const [selectedTierDuration, setSelectedTierDuration] = useState(null); // null = no expiry
  const [showReviewManager, setShowReviewManager] = useState(false);
  const [showPhotoApprovals, setShowPhotoApprovals] = useState(false);
  const [photoApprovalUserId, setPhotoApprovalUserId] = useState(null);
  const [pendingPhotoCount, setPendingPhotoCount] = useState(0);
  const [showSponsoredManager, setShowSponsoredManager] = useState(false);
  const [showPromoManager, setShowPromoManager] = useState(false);
  const [showBackupManager, setShowBackupManager] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    doctors: 0,
    providers: 0,
    patients: 0
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    userType: 'user',
    specialization: '',
    clinicName: '',
    licenseNumber: '',
    providerCategory: 'Healthcare Clinic' // Default provider category
  });

  useEffect(() => {
    if (user?.user_type === 'admin') {
      getAdminTokenAndFetchData();
      fetchPendingPhotoCount();
    }
  }, [user]);

  const fetchPendingPhotoCount = async () => {
    try {
      const token = localStorage.getItem('sessionToken');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photo-approvals?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPendingPhotoCount(data.pending_count || 0);
      }
    } catch (err) {
      console.error('Error fetching pending photo count:', err);
    }
  };

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, searchQuery]);

  const getAdminToken = async () => {
    // Use the current user's session token from localStorage
    const sessionToken = localStorage.getItem('sessionToken');
    
    if (!sessionToken) {
      console.error('❌ No session token found in localStorage');
      setError('Admin authentication failed: No session token found');
      return null;
    }
    
    console.log('✅ Using current admin session token');
    setAdminToken(sessionToken);
    return sessionToken;
  };

  const getAdminTokenAndFetchData = async () => {
    try {
      await getAdminToken();
      await Promise.all([fetchUsers(), fetchStats()]);
    } catch (error) {
      console.error('Error getting admin data:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = await getAdminToken();
      
      if (!token) {
        console.error('❌ No admin token available, cannot fetch users');
        setError('Cannot fetch users: Admin authentication failed');
        setLoading(false);
        return;
      }
      
      console.log('🔍 Fetching users with token:', token.substring(0, 20) + '...');
      
      // Fetch all users (all users are active by default)
      const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Users API response status:', usersResponse.status);
      
      // Fetch doctors from doctors collection
      const doctorsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doctors`);
      
      // Fetch providers from providers collection
      const providersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare-providers?include_unverified=true&limit=200`);

      let allUsers = [];
      
      // Add users from users collection
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        console.log('✅ Users API response data:', usersData);
        const users = (usersData.users || []).map(user => ({
          ...user,
          source: 'users_collection',
          display_type: user.user_type || 'user'
        }));
        allUsers = [...allUsers, ...users];
        console.log('✅ Loaded users from users collection:', users.length);
      } else {
        const errorText = await usersResponse.text();
        console.error('❌ Failed to fetch users:', usersResponse.status, errorText);
        setError(`Failed to fetch users: ${usersResponse.status} - ${errorText}`);
      }
      
      // Add doctors from doctors collection (only if not already in users)
      if (doctorsResponse.ok) {
        const doctorsData = await doctorsResponse.json();
        const existingUserIds = new Set(allUsers.map(u => u.id));
        
        const doctors = (doctorsData.doctors || [])
          .filter(doctor => !existingUserIds.has(doctor.user_id)) // Exclude if user already exists
          .map(doctor => ({
            ...doctor,
            user_type: 'doctor',
            source: 'doctors_collection',
            display_type: 'doctor',
            // Map doctor fields to user-like structure
            email: doctor.email || `${doctor.name?.toLowerCase().replace(/\s+/g, '.')}@clinic.com`,
            phone: doctor.phone || 'N/A'
          }));
        allUsers = [...allUsers, ...doctors];
        console.log('Loaded doctors from doctors collection:', doctors.length);
      }
      
      // Merge providers data with users - providers have subscription_tier info
      if (providersResponse.ok) {
        const providersData = await providersResponse.json();
        
        // Create a map of provider data by user_id for quick lookup
        const providersByUserId = {};
        (providersData.providers || []).forEach(provider => {
          if (provider.user_id) {
            providersByUserId[provider.user_id] = provider;
          }
        });
        
        // Merge provider data into existing users (to get subscription_tier)
        allUsers = allUsers.map(user => {
          const providerData = providersByUserId[user.id];
          if (providerData && user.user_type === 'provider') {
            // Merge provider-specific fields like subscription_tier
            return {
              ...user,
              subscription_tier: providerData.subscription_tier || user.subscription_tier,
              subscription_tier_id: providerData.subscription_tier_id || user.subscription_tier_id,
              tier_updated_at: providerData.tier_updated_at || user.tier_updated_at,
              tier_expires_at: providerData.tier_expires_at || user.tier_expires_at,
              tier_duration_months: providerData.tier_duration_months || user.tier_duration_months,
              provider_id: providerData.id // Store provider ID for API calls
            };
          }
          return user;
        });
        
        // Add providers that don't have a user entry
        const existingUserIds = new Set(allUsers.map(u => u.id));
        const newProviders = (providersData.providers || [])
          .filter(provider => !existingUserIds.has(provider.user_id))
          .map(provider => ({
            ...provider,
            user_type: 'provider',
            source: 'providers_collection',
            display_type: 'provider',
            email: provider.email || `${provider.name?.toLowerCase().replace(/\s+/g, '.')}@provider.com`,
            phone: provider.phone || 'N/A'
          }));
        allUsers = [...allUsers, ...newProviders];
        console.log('Merged providers data, added new providers:', newProviders.length);
      }
      
      setUsers(allUsers);
      setFilteredUsers(allUsers);
      console.log('Total loaded profiles:', allUsers.length);
      
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = await getAdminToken();
      
      if (!token) {
        console.error('❌ No admin token available, cannot fetch stats');
        setError('Cannot fetch statistics: Admin authentication failed');
        return;
      }
      
      console.log('🔍 Fetching stats with token:', token.substring(0, 20) + '...');
      
      // First, let's check what the doctors endpoint returns
      const doctorsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/doctors`);
      const doctorsData = await doctorsResponse.json();
      console.log('Raw doctors data:', doctorsData);
      console.log('Number of doctors from doctors endpoint:', doctorsData.doctors?.length || 0);
      
      // Check providers endpoint  
      const providersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare-providers?include_unverified=true&limit=200`);
      const providersData = await providersResponse.json();
      console.log('Raw providers data:', providersData);
      console.log('Number of providers from providers endpoint:', providersData.providers?.length || 0);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Stats API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Stats API response:', data);
        
        if (data.success && data.stats) {
          setStats({
            totalUsers: data.stats.totalUsers || 0,
            doctors: data.stats.doctors || 0,
            providers: data.stats.providers || 0,
            patients: data.stats.patients || 0,
            admins: data.stats.admins || 0
          });
        } else {
          // Fallback to manual calculation if needed
          const fallbackStats = {
            totalUsers: (doctorsData.doctors?.length || 0) + (providersData.providers?.length || 0) + 1,
            doctors: doctorsData.doctors?.length || 0,
            providers: providersData.providers?.length || 0,
            patients: 0
          };
          setStats(fallbackStats);
        }
      } else {
        console.error('Failed to fetch stats:', response.status, await response.text());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Filter Functions with Search
  const filterUsers = (filterType) => {
    console.log('Filtering users by:', filterType, 'Total users:', users.length);
    setActiveFilter(filterType);
    
    let filtered = [];
    switch (filterType) {
      case 'all':
        filtered = users;
        break;
      case 'doctors':
        filtered = users.filter(user => user.display_type === 'doctor' || user.user_type === 'doctor');
        break;
      case 'providers':
        filtered = users.filter(user => user.display_type === 'provider' || user.user_type === 'provider');
        break;
      case 'patients':
        filtered = users.filter(user => 
          user.display_type === 'user' || 
          user.user_type === 'user' || 
          user.user_type === 'patient' ||
          (!user.display_type && !user.user_type)
        );
        break;
      default:
        filtered = users;
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(user => 
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.user_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.display_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.speciality?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    console.log('Filtered result:', filtered.length, 'users for filter:', filterType, 'search:', searchQuery);
    setFilteredUsers(filtered);
  };

  // Search function
  const handleSearch = (query) => {
    setSearchQuery(query);
    filterUsers(activeFilter); // Re-apply current filter with search
  };

  // Delete user functions
  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setDeleteConfirmation('');
  };

  const confirmDeleteUser = async () => {
    if (deleteConfirmation !== 'DELETE') {
      alert('Please type "DELETE" to confirm');
      return;
    }

    try {
      const token = await getAdminToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/delete-user/${userToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteConfirmation('');
        
        // Refresh data
        await Promise.all([fetchUsers(), fetchStats()]);
        
        alert(`✅ User "${userToDelete.name}" has been permanently deleted.`);
      } else {
        const errorData = await response.json();
        alert(`❌ Error deleting user: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`❌ Error deleting user: ${error.message}`);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setDeleteConfirmation('');
  };

  const handleEditUser = async (user) => {
    try {
      const token = await getAdminToken();
      
      // Fetch detailed user information
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userDetails = data.user;
        
        setEditUserData({
          id: userDetails.id,
          name: userDetails.name || userDetails.facility_name || '',
          email: userDetails.email || '',
          phone: userDetails.phone || '',
          date_of_birth: userDetails.date_of_birth || '',
          gender: userDetails.gender || '',
          user_type: userDetails.user_type || 'user',
          insurance_company: userDetails.insurance_company || '',
          insurance_number: userDetails.insurance_number || '',
          policy_holder_name: userDetails.policy_holder_name || '',
          group_number: userDetails.group_number || '',
          picture: userDetails.picture || ''
        });
        
        setUserAppointments(userDetails.appointments || []);
        setSelectedUser(user);
        setShowEditUser(true);
      } else {
        alert('Failed to fetch user details');
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert('Error fetching user details');
    }
  };

  // Open tier modal for a provider
  const openTierModal = (user, newTier) => {
    setTierModalUser({ ...user, pendingTier: newTier });
    setSelectedTierDuration(null); // Reset duration
  };

  // Close tier modal
  const closeTierModal = () => {
    setTierModalUser(null);
    setSelectedTierDuration(null);
  };

  // Confirm tier change with duration
  const handleTierChange = async (userId, newTier, duration = null) => {
    try {
      const token = await getAdminToken();
      
      // Find the user to get the provider_id if available
      const user = users.find(u => u.id === userId);
      const apiId = user?.provider_id || userId;
      
      // Build query params
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/providers/${apiId}/tier?tier_name=${newTier}`;
      if (duration) {
        url += `&duration_months=${duration}`;
      }
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the local state for both the user ID and provider ID
        setUsers(prevUsers => prevUsers.map(u => 
          (u.id === userId || u.provider_id === apiId) ? { 
            ...u, 
            subscription_tier: newTier,
            tier_expires_at: result.expires_at,
            tier_duration_months: result.duration_months
          } : u
        ));
        setFilteredUsers(prevFiltered => prevFiltered.map(u =>
          (u.id === userId || u.provider_id === apiId) ? { 
            ...u, 
            subscription_tier: newTier,
            tier_expires_at: result.expires_at,
            tier_duration_months: result.duration_months
          } : u
        ));
        
        let successMsg = `✅ Provider tier updated to ${newTier} successfully!`;
        if (result.expires_at) {
          successMsg += `\n\nExpires: ${new Date(result.expires_at).toLocaleDateString()}`;
        }
        successMsg += `\n\nLimits:\n- Max Staff: ${result.limits.max_staff}\n- Max Services: ${result.limits.max_services}\n- Booking Window: ${result.limits.booking_window_months} months`;
        
        alert(successMsg);
        closeTierModal();
      } else {
        const errorData = await response.json();
        alert(`❌ Error updating tier: ${errorData.detail || 'Unknown error'}`);
        // Refresh to get correct state
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error updating provider tier:', error);
      alert(`❌ Error updating provider tier: ${error.message}`);
      // Refresh to get correct state
      await fetchUsers();
    }
  };


  const saveUserChanges = async (e) => {
    e.preventDefault();
    try {
      const token = await getAdminToken();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${editUserData.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editUserData)
      });

      if (response.ok) {
        const result = await response.json();
        setShowEditUser(false);
        setSelectedUser(null);
        setEditUserData({});
        
        // Refresh user list
        await Promise.all([fetchUsers(), fetchStats()]);
        
        alert('✅ User updated successfully!');
      } else {
        const errorData = await response.json();
        alert(`❌ Error updating user: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert(`❌ Error updating user: ${error.message}`);
    }
  };

  const handleAppointmentAction = async (appointmentId, action) => {
    try {
      const token = await getAdminToken();
      let url, method, body = null;

      if (action === 'cancel') {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/appointment/${appointmentId}`;
        method = 'DELETE';
      } else if (action === 'update') {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/appointment/${appointmentId}`;
        method = 'PUT';
        body = JSON.stringify({ status: 'confirmed' });
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body
      });

      if (response.ok) {
        alert(`✅ Appointment ${action}ed successfully!`);
        // Refresh user details
        if (selectedUser) {
          handleEditUser(selectedUser);
        }
      } else {
        const errorData = await response.json();
        alert(`❌ Error: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error with appointment action:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  // Update filtered users when users data or search query changes
  useEffect(() => {
    filterUsers(activeFilter);
  }, [users, searchQuery]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const token = await getAdminToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });

      if (response.ok) {
        const result = await response.json();
        setShowCreateUser(false);
        setNewUser({
          name: '',
          email: '',
          password: '',
          userType: 'user',
          phone: '',
          specialization: '',
          clinicName: '',
          licenseNumber: '',
          providerCategory: 'Healthcare Clinic'
        });
        
        console.log('User created:', result);
        
        // Refresh data to show new user
        await Promise.all([fetchUsers(), fetchStats()]);
        
        // Show success message based on user type
        if (newUser.userType === 'doctor') {
          alert(`✅ Doctor created successfully!\n\nThe doctor "${newUser.name}" will now appear in:\n• Admin users list\n• User-facing doctors page for booking`);
        } else if (newUser.userType === 'provider') {
          alert(`✅ Provider created successfully!\n\nThe provider "${newUser.name}" will now appear in:\n• Admin users list\n• User-facing providers page for booking`);
        } else {
          alert(`✅ User created successfully!`);
        }
      } else {
        const errorData = await response.json();
        alert(`❌ Error creating user: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert(`❌ Error creating user: ${error.message}`);
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const token = await getAdminToken(); // Use admin token instead of regular session token
      
      if (!token) {
        alert('❌ Admin authentication required for impersonation');
        return;
      }
      
      console.log('🔍 Attempting to impersonate user:', userId);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/impersonate/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Impersonate API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Impersonation successful, new token received');
        
        // Store the new impersonated user token
        localStorage.setItem('sessionToken', data.session_token);
        
        // Clear admin token cache so we don't interfere with impersonated session
        setAdminToken(null);
        
        // Redirect to home page as the impersonated user
        window.location.href = '/';
      } else {
        const errorData = await response.json();
        console.error('❌ Impersonation failed:', errorData);
        alert(`❌ Failed to impersonate user: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error impersonating user:', error);
      alert(`❌ Error impersonating user: ${error.message}`);
    }
  };

  // toggleUserStatus function removed - all users are active by default

  const fetchPendingDoctors = async () => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verification-queue?user_type=doctor`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingDoctors(data.queue || []);
        console.log(`Found ${data.count} pending doctors in verification queue`);
      }
    } catch (error) {
      console.error('Error fetching pending doctors:', error);
    }
  };

  const fetchPendingProviders = async () => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/verification-queue?user_type=provider`,
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingProviders(data.queue || []);
        console.log(`Found ${data.count} pending providers in verification queue`);
      }
    } catch (error) {
      console.error('Error fetching pending providers:', error);
    }
  };

  const verifyDoctor = async (doctorId, verified = true) => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      let updateData;
      if (verified) {
        // Approve: Set verified to true and list on website
        updateData = { is_verified: true, is_active: true, is_blocked: false, is_listed: true };
      } else {
        // Reject/Block: Set verified to false, blocked to true, deactivate, unlist
        updateData = { 
          is_verified: false, 
          is_active: false, 
          is_blocked: true,
          is_listed: false,
          block_reason: 'Rejected by admin during verification process'
        };
      }

      // Use the edit-user endpoint to update the verification and block status
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${doctorId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert(`✅ Doctor ${verified ? 'verified' : 'rejected and blocked'} successfully`);
        fetchPendingDoctors(); // Refresh the list
        fetchStats(); // Update stats
      } else {
        const errorData = await response.json();
        alert(`❌ Failed to ${verified ? 'verify' : 'reject'} doctor: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error verifying doctor:', error);
      alert('❌ Error verifying doctor');
    }
  };

  const verifyProvider = async (providerId, verified = true) => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      let updateData;
      if (verified) {
        // Approve: Set verified to true and list on website
        updateData = { is_verified: true, is_active: true, is_blocked: false, is_listed: true };
      } else {
        // Reject/Block: Set verified to false, blocked to true, deactivate, unlist
        updateData = { 
          is_verified: false, 
          is_active: false, 
          is_blocked: true,
          is_listed: false,
          block_reason: 'Rejected by admin during verification process'
        };
      }

      // Use the edit-user endpoint to update the verification and block status
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${providerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert(`✅ Provider ${verified ? 'verified' : 'rejected and blocked'} successfully`);
        fetchPendingProviders(); // Refresh the list
        fetchStats(); // Update stats
      } else {
        const errorData = await response.json();
        alert(`❌ Failed to ${verified ? 'verify' : 'reject'} provider: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error verifying provider:', error);
      alert('❌ Error verifying provider');
    }
  };

  const toggleListingStatus = async (userId, isCurrentlyListed) => {
    try {
      const token = await getAdminToken();
      if (!token) return;

      const newStatus = !isCurrentlyListed;
      
      // Confirm action with admin
      const action = newStatus ? 'list on website' : 'remove from website';
      if (!window.confirm(`Are you sure you want to ${action} this doctor/provider?`)) {
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_listed: newStatus })
      });

      if (response.ok) {
        alert(`✅ Successfully ${newStatus ? 'listed' : 'unlisted'} from website`);
        fetchUsers(); // Refresh the user list
      } else {
        const errorData = await response.json();
        alert(`❌ Failed to update listing status: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling listing status:', error);
      alert('❌ Error updating listing status');
    }
  };

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      const token = await getAdminToken();
      
      if (!token) {
        setError('Admin authentication required for search');
        setIsSearching(false);
        return;
      }

      // Get all users and filter client-side for comprehensive search
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const users = data.users || [];
        
        // Comprehensive search across multiple fields
        const filteredUsers = users.filter(user => {
          const searchTerm = query.toLowerCase();
          const firstName = (user.first_name || '').toLowerCase();
          const lastName = (user.last_name || '').toLowerCase();
          const fullName = `${firstName} ${lastName}`.trim();
          const facilityName = (user.facility_name || '').toLowerCase();
          const email = (user.email || '').toLowerCase();
          const phone = (user.phone || '').toLowerCase();
          const userType = (user.user_type || '').toLowerCase();
          const specialization = (user.specialization || '').toLowerCase();
          
          return (
            firstName.includes(searchTerm) ||
            lastName.includes(searchTerm) ||
            fullName.includes(searchTerm) ||
            facilityName.includes(searchTerm) ||
            email.includes(searchTerm) ||
            phone.includes(searchTerm) ||
            userType.includes(searchTerm) ||
            specialization.includes(searchTerm)
          );
        });

        setSearchResults(filteredUsers);
        console.log(`🔍 Found ${filteredUsers.length} users matching "${query}"`);
      } else {
        setError('Failed to search users');
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setError('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search to avoid too many API calls
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setUserSearchQuery(query);
    
    // Clear previous timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    // Set new timeout for debounced search
    window.searchTimeout = setTimeout(() => {
      searchUsers(query);
    }, 300);
  };

  // Temporarily disable admin check for testing purposes
  /*
  if (user?.user_type !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }
  */

  return (
    <div className="min-h-screen bg-slate-50 lg:flex" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Sidebar navigation */}
      {(() => {
        const closeAllPanels = () => {
          setShowDoctorVerification(false); setShowProviderVerification(false);
          setShowContactChangeRequests(false); setShowAnalytics(false);
          setShowReviewManager(false); setShowSponsoredManager(false);
          setShowPromoManager(false); setShowBackupManager(false); setShowUserSearch(false);
          setShowPhotoApprovals(false); setPhotoApprovalUserId(null);
        };
        const navGroups = [
          { group: 'Overview & Core', items: [
            { id: 'overview', label: t('adminPanel.title', 'Dashboard'), icon: LayoutDashboard, onClick: closeAllPanels },
            { id: 'create_user', label: t('adminPanel.createNewUser', 'Create User'), icon: UserPlus, onClick: () => setShowCreateUser(true) },
            { id: 'search', label: t('adminPanel.searchUsers', 'Search Users'), icon: Search, onClick: () => setShowUserSearch(true) },
          ]},
          { group: 'Verification & Requests', items: [
            { id: 'doctor_verification', label: t('adminPanel.manageDoctorVerification', 'Doctor Verifications'), icon: Stethoscope, badge: (pendingDoctors?.length || 0) || null, onClick: () => setShowDoctorVerification(true) },
            { id: 'provider_verification', label: t('adminPanel.manageProviderVerification', 'Provider Verifications'), icon: Building2, badge: (pendingProviders?.length || 0) || null, onClick: () => setShowProviderVerification(true) },
            { id: 'contact_requests', label: 'Contact Changes', icon: PhoneCall, testid: 'admin-contact-change-requests-btn', onClick: () => setShowContactChangeRequests(true) },
            { id: 'photo_approvals', label: 'Photo Approvals', icon: ImageIcon, badge: pendingPhotoCount || null, testid: 'admin-photo-approvals-btn', onClick: () => { setPhotoApprovalUserId(null); setShowPhotoApprovals(true); } },
          ]},
          { group: 'Content & Operations', items: [
            { id: 'reviews', label: t('adminPanel.manageReviews', 'Review Moderation'), icon: MessageSquareQuote, testid: 'admin-manage-reviews-btn', onClick: () => setShowReviewManager(true) },
            { id: 'sponsored', label: t('adminPanel.manageSponsored', 'Sponsored Providers'), icon: Sparkles, testid: 'admin-manage-sponsored-btn', onClick: () => setShowSponsoredManager(true) },
            { id: 'promos', label: t('adminPanel.promoCodes', 'Promo Codes'), icon: Ticket, testid: 'admin-manage-promos-btn', onClick: () => setShowPromoManager(true) },
          ]},
          { group: 'System & Intelligence', items: [
            { id: 'analytics', label: t('adminPanel.analytics', 'Analytics & Reports'), icon: TrendingUp, onClick: () => setShowAnalytics(true) },
            { id: 'backups', label: t('adminPanel.systemBackups', 'System Backups'), icon: HardDriveDownload, testid: 'admin-backups-btn', onClick: () => setShowBackupManager(true) },
          ]},
        ];
        return (
          <aside className="lg:w-64 lg:shrink-0 lg:h-screen lg:sticky lg:top-0 bg-[#090D16] text-slate-300 flex flex-col z-20" data-testid="admin-sidebar">
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white font-bold text-sm">E</div>
              <div className="font-semibold text-white text-sm">EnayaHub <span className="text-slate-400 font-normal">Admin</span></div>
            </div>
            <nav className="flex-1 lg:overflow-y-auto overflow-x-auto p-3 flex lg:flex-col gap-1">
              {navGroups.map((grp) => (
                <div key={grp.group} className="lg:mb-3 shrink-0">
                  <p className="hidden lg:block px-3 mb-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{grp.group}</p>
                  <div className="flex lg:flex-col gap-1">
                    {grp.items.map((item) => {
                      const Icon = item.icon;
                      const active = activeNav === item.id;
                      return (
                        <button
                          key={item.id}
                          data-testid={item.testid}
                          onClick={() => { setActiveNav(item.id); item.onClick(); }}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${active ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                        >
                          <Icon className="w-4 h-4 shrink-0" />
                          <span>{item.label}</span>
                          {item.badge ? <span className="ms-auto text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5 font-semibold">{item.badge}</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </aside>
        );
      })()}
      <div className="flex-1 min-w-0 w-full max-w-full mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 sm:mb-6 lg:mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{t('adminPanel.title')}</h1>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600 mt-1 sm:mt-2">{t('adminPanel.subtitle')}</p>
            </div>
            <a
              href="/admin/subscriptions"
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg"
            >
              {t('adminPanel.subscriptions')}
            </a>
          </div>
          
          {/* Error Display */}
          {error && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-start">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-red-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-medium text-red-800">Admin Panel Error</h3>
                  <div className="mt-1 text-xs sm:text-sm text-red-700 break-words">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Cards - Interactive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
          {/* Total Users */}
          <div 
            onClick={() => filterUsers('all')}
            className={`bg-white rounded-lg p-4 sm:p-5 lg:p-6 shadow cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              activeFilter === 'all' ? 'ring-2 ring-blue-500 bg-blue-50' : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`p-2 sm:p-3 rounded-full ${activeFilter === 'all' ? 'bg-blue-200' : 'bg-blue-100'} flex-shrink-0`}>
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className={`text-xs sm:text-sm font-medium ${activeFilter === 'all' ? 'text-blue-700' : 'text-gray-600'} truncate`}>
                  {t('adminPanel.allProfiles')}
                </p>
                <p className={`text-xl sm:text-2xl font-semibold ${activeFilter === 'all' ? 'text-blue-900' : 'text-gray-900'}`}>
                  {stats.totalUsers}
                </p>
              </div>
            </div>
            {activeFilter === 'all' && (
              <div className="mt-2 text-xs text-blue-600 font-medium">● {t('adminPanel.activeFilter')}</div>
            )}
          </div>

          {/* Doctors */}
          <div 
            onClick={() => filterUsers('doctors')}
            className={`bg-white rounded-lg p-4 sm:p-5 lg:p-6 shadow cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              activeFilter === 'doctors' ? 'ring-2 ring-green-500 bg-green-50' : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`p-2 sm:p-3 rounded-full ${activeFilter === 'doctors' ? 'bg-green-200' : 'bg-green-100'} flex-shrink-0`}>
                <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className={`text-xs sm:text-sm font-medium ${activeFilter === 'doctors' ? 'text-green-700' : 'text-gray-600'} truncate`}>
                  {t('adminPanel.doctors')}
                </p>
                <p className={`text-xl sm:text-2xl font-semibold ${activeFilter === 'doctors' ? 'text-green-900' : 'text-gray-900'}`}>
                  {stats.doctors}
                </p>
              </div>
            </div>
            {activeFilter === 'doctors' && (
              <div className="mt-2 text-xs text-green-600 font-medium">● {t('adminPanel.activeFilter')}</div>
            )}
          </div>

          {/* Providers */}
          <div 
            onClick={() => filterUsers('providers')}
            className={`bg-white rounded-lg p-4 sm:p-5 lg:p-6 shadow cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              activeFilter === 'providers' ? 'ring-2 ring-purple-500 bg-purple-50' : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`p-2 sm:p-3 rounded-full ${activeFilter === 'providers' ? 'bg-purple-200' : 'bg-purple-100'} flex-shrink-0`}>
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className={`text-xs sm:text-sm font-medium ${activeFilter === 'providers' ? 'text-purple-700' : 'text-gray-600'} truncate`}>
                  {t('adminPanel.providers')}
                </p>
                <p className={`text-xl sm:text-2xl font-semibold ${activeFilter === 'providers' ? 'text-purple-900' : 'text-gray-900'}`}>
                  {stats.providers}
                </p>
              </div>
            </div>
            {activeFilter === 'providers' && (
              <div className="mt-2 text-xs text-purple-600 font-medium">● {t('adminPanel.activeFilter')}</div>
            )}
          </div>

          {/* Patients */}
          <div 
            onClick={() => filterUsers('patients')}
            className={`bg-white rounded-lg p-4 sm:p-5 lg:p-6 shadow cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 ${
              activeFilter === 'patients' ? 'ring-2 ring-orange-500 bg-orange-50' : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`p-2 sm:p-3 rounded-full ${activeFilter === 'patients' ? 'bg-orange-200' : 'bg-orange-100'} flex-shrink-0`}>
                <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
              </div>
              <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                <p className={`text-xs sm:text-sm font-medium ${activeFilter === 'patients' ? 'text-orange-700' : 'text-gray-600'} truncate`}>
                  {t('adminPanel.users')}
                </p>
                <p className={`text-xl sm:text-2xl font-semibold ${activeFilter === 'patients' ? 'text-orange-900' : 'text-gray-900'}`}>
                  {stats.patients}
                </p>
              </div>
            </div>
            {activeFilter === 'patients' && (
              <div className="mt-2 text-xs text-orange-600 font-medium">● {t('adminPanel.activeFilter')}</div>
            )}
          </div>
        </div>

        {/* Quick actions now live in the left sidebar navigation */}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-3 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 space-y-2 sm:space-y-0">
              <h3 className="text-lg font-medium text-gray-900">
                {activeFilter === 'all' ? t('adminPanel.allUsers') :
                 activeFilter === 'doctors' ? t('adminPanel.doctors') :
                 activeFilter === 'providers' ? t('adminPanel.providers') :
                 activeFilter === 'patients' ? t('adminPanel.users') : t('adminPanel.users')}
                <span className="ml-2 text-sm text-gray-500">
                  ({filteredUsers.length})
                </span>
              </h3>
              {activeFilter !== 'all' && (
                <button
                  onClick={() => filterUsers('all')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium self-start sm:self-auto"
                >
                  {t('adminPanel.allUsers')}
                </button>
              )}
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={t('adminPanel.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {searchQuery && (
                <button
                  onClick={() => handleSearch('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Sorting Controls */}
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <label className="text-sm font-medium text-gray-700">{t('adminPanel.sortBy')}:</label>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="date">{t('adminPanel.dateCreated')}</option>
                  <option value="type">{t('adminPanel.type')}</option>
                  <option value="tier">Subscription Tier</option>
                  <option value="name">Name</option>
                </select>
                
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors flex items-center gap-1"
                  title={sortOrder === 'asc' ? t('adminPanel.ascending') : t('adminPanel.descending')}
                >
                  {sortOrder === 'asc' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                      <span className="hidden sm:inline">{t('adminPanel.ascending')}</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      <span className="hidden sm:inline">{t('adminPanel.descending')}</span>
                    </>
                  )}
                </button>
                
                {(sortBy !== 'date' || sortOrder !== 'desc') && (
                  <button
                    onClick={() => {
                      setSortBy('date');
                      setSortOrder('desc');
                    }}
                    className="px-3 py-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Pagination calculations */}
          {(() => {
            // Apply sorting
            const sortedUsers = [...filteredUsers].sort((a, b) => {
              let comparison = 0;
              
              switch (sortBy) {
                case 'date':
                  const dateA = new Date(a.created_at || 0);
                  const dateB = new Date(b.created_at || 0);
                  comparison = dateA - dateB;
                  break;
                  
                case 'type':
                  const typeA = (a.user_type || a.type || 'user').toLowerCase();
                  const typeB = (b.user_type || b.type || 'user').toLowerCase();
                  comparison = typeA.localeCompare(typeB);
                  break;
                  
                case 'tier':
                  const tierOrder = { 'Free': 1, 'Basic': 2, 'Premium': 3, 'Enterprise': 4 };
                  const tierA = tierOrder[a.subscription_tier] || 0;
                  const tierB = tierOrder[b.subscription_tier] || 0;
                  comparison = tierA - tierB;
                  break;
                  
                case 'name':
                  const nameA = (a.name || a.first_name || '').toLowerCase();
                  const nameB = (b.name || b.first_name || '').toLowerCase();
                  comparison = nameA.localeCompare(nameB);
                  break;
                  
                default:
                  comparison = 0;
              }
              
              return sortOrder === 'asc' ? comparison : -comparison;
            });
            
            const indexOfLastUser = currentPage * usersPerPage;
            const indexOfFirstUser = indexOfLastUser - usersPerPage;
            const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
            const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
            
            return (
              <>
                {/* Mobile: Card Layout, Desktop: Table Layout */}
                <div className="block sm:hidden">
                  {/* Mobile Card Layout */}
                  {currentUsers.map((user) => (
              <div key={user.id} className="border-b border-gray-200 p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-shrink-0">
                        <span className="inline-block h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium text-gray-700">
                          {(user.first_name || user.facility_name || user.email || 'U')[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.user_type === 'provider' ? 
                            (user.facility_name || 'Unnamed Facility') : 
                            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed User'
                          }
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        user.user_type === 'doctor' ? 'bg-blue-100 text-blue-800' :
                        user.user_type === 'provider' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {user.user_type === 'patient' ? 'User' : 
                         user.user_type?.charAt(0).toUpperCase() + user.user_type?.slice(1)}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Created: {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-col space-y-1 ml-2">
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setEditUserData({
                          first_name: user.first_name || '',
                          last_name: user.last_name || '',
                          email: user.email || '',
                          phone: user.phone || '',
                          age: user.age || '',
                          gender: user.gender || '',
                          user_type: user.user_type || 'user',
                          insurance_company: user.insurance_company || '',
                          insurance_number: user.insurance_number || '',
                          insurance_type: user.insurance_type || '',
                          coverage_type: user.coverage_type || '',
                          policy_holder_name: user.policy_holder_name || ''
                        });
                      }}
                      className="text-xs text-blue-600 hover:text-blue-900 font-medium px-2 py-1 bg-blue-50 rounded"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => impersonateUser(user.id)}
                      className="text-xs text-green-600 hover:text-green-900 font-medium px-2 py-1 bg-green-50 rounded"
                    >
                      Login As
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table Layout */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.type')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.tier')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.created')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminPanel.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      {activeFilter === 'all' ? 'No users found' : `No ${activeFilter} found`}
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-sm font-medium text-gray-700">
                                {user.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.user_type === 'admin' ? 'bg-purple-100 text-purple-800' :
                          user.user_type === 'doctor' ? 'bg-green-100 text-green-800' :
                          user.user_type === 'provider' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.user_type === 'patient' ? 'User' : 
                           user.user_type === 'user' ? 'User' : 
                           user.user_type?.charAt(0).toUpperCase() + user.user_type?.slice(1) || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.user_type === 'provider' ? (
                          <div className="flex flex-col gap-1">
                            <select
                              value={user.subscription_tier || 'Free'}
                              onChange={(e) => openTierModal(user, e.target.value)}
                              className="text-xs font-semibold rounded px-2 py-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Free">Free</option>
                              <option value="Basic">Basic</option>
                              <option value="Pro">Pro</option>
                              <option value="Enterprise">Enterprise</option>
                            </select>
                            {user.tier_expires_at && (
                              <span className="text-xs text-orange-600">
                                Expires: {new Date(user.tier_expires_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          {t('adminPanel.active')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            {t('adminPanel.edit')}
                          </button>
                          <button
                            onClick={() => impersonateUser(user.id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t('adminPanel.loginAs')}
                          </button>
                          {/* List/Unlist button for doctors and providers only */}
                          {(user.user_type === 'doctor' || user.user_type === 'provider') && user.is_verified && (
                            <button
                              onClick={() => toggleListingStatus(user.id, user.is_listed !== false)}
                              className={user.is_listed !== false ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                              title={user.is_listed !== false ? 'Remove from website' : 'Show on website'}
                            >
                              {user.is_listed !== false ? 'Unlist' : 'List'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900"
                          >
                            {t('adminPanel.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {filteredUsers.length > usersPerPage && (
            <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Page Info */}
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstUser + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastUser, filteredUsers.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredUsers.length}</span> users
                </div>
                
                {/* Pagination Buttons */}
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {totalPages > 5 && currentPage < totalPages - 2 && (
                      <>
                        <span className="px-2 text-gray-500">...</span>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                        >
                          {totalPages}
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Next Button */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
        );
      })()}
        </div>

        {/* Create User Modal */}
        {showCreateUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowCreateUser(false)}></div>
              
              <div className="inline-block w-full max-w-lg overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle">
                <form onSubmit={createUser}>
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">Create New User</h3>
                  </div>
                  
                  <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">User Type</label>
                      <select
                        value={newUser.userType}
                        onChange={(e) => setNewUser({...newUser, userType: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="user">User</option>
                        <option value="doctor">Doctor</option>
                        <option value="provider">Provider</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Password</label>
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newUser.phone}
                        onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                        className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    
                    {newUser.userType === 'doctor' && (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Specialization</label>
                          <input
                            type="text"
                            value={newUser.specialization}
                            onChange={(e) => setNewUser({...newUser, specialization: e.target.value})}
                            className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">License Number</label>
                          <input
                            type="text"
                            value={newUser.licenseNumber}
                            onChange={(e) => setNewUser({...newUser, licenseNumber: e.target.value})}
                            className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    )}
                    
                    {newUser.userType === 'provider' && (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Provider Category</label>
                          <select
                            value={newUser.providerCategory}
                            onChange={(e) => setNewUser({...newUser, providerCategory: e.target.value})}
                            className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="Hospital">Hospital</option>
                            <option value="Healthcare Clinic">Healthcare Clinic</option>
                            <option value="Beauty Clinic">Beauty Clinic</option>
                            <option value="Dental Clinic">Dental Clinic</option>
                            <option value="Spa">Spa</option>
                            <option value="Salon">Salon (Ladies)</option>
                            <option value="Barbershop">Barbershop (Men)</option>
                            <option value="Pharmacy">Pharmacy</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Clinic Name</label>
                          <input
                            type="text"
                            value={newUser.clinicName}
                            onChange={(e) => setNewUser({...newUser, clinicName: e.target.value})}
                            className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">License Number</label>
                          <input
                            type="text"
                            value={newUser.licenseNumber}
                            onChange={(e) => setNewUser({...newUser, licenseNumber: e.target.value})}
                            className="w-full px-2 sm:px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={() => setShowCreateUser(false)}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Create User
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Tier Duration Modal */}
        {tierModalUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeTierModal}></div>
              
              <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Update Subscription Tier
                    </h3>
                    <button onClick={closeTierModal} className="text-gray-400 hover:text-gray-600">
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      Provider: <span className="font-medium text-gray-900">{tierModalUser.name || tierModalUser.email}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Current Tier: <span className="font-medium text-gray-900">{tierModalUser.subscription_tier || 'Free'}</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      New Tier: <span className="font-medium text-blue-600">{tierModalUser.pendingTier}</span>
                    </p>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subscription Duration
                    </label>
                    <select
                      value={selectedTierDuration || ''}
                      onChange={(e) => setSelectedTierDuration(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">No Expiry (Permanent)</option>
                      <option value="1">1 Month</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">12 Months (1 Year)</option>
                    </select>
                    {selectedTierDuration && (
                      <p className="mt-2 text-sm text-gray-500">
                        Tier will expire on: {new Date(Date.now() + selectedTierDuration * 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={closeTierModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleTierChange(tierModalUser.id, tierModalUser.pendingTier, selectedTierDuration)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Update Tier
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={cancelDelete}></div>
              
              <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.632 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">Delete User</h3>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-4">
                      You are about to permanently delete the following user:
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700">
                              {userToDelete.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{userToDelete.name}</div>
                          <div className="text-sm text-gray-500">{userToDelete.email}</div>
                          <div className="text-xs text-gray-500">Type: {userToDelete.user_type}</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">Warning: This action cannot be undone!</h3>
                          <div className="mt-2 text-sm text-red-700">
                            <p>This will permanently delete:</p>
                            <ul className="list-disc list-inside mt-1">
                              <li>User account and profile</li>
                              <li>All related data (appointments, messages, etc.)</li>
                              <li>Associated provider/doctor profiles</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To confirm deletion, please type <span className="font-bold text-red-600">DELETE</span> below:
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="Type DELETE to confirm"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteUser}
                    disabled={deleteConfirmation !== 'DELETE'}
                    className={`px-4 py-2 text-white rounded-lg ${
                      deleteConfirmation === 'DELETE' 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Delete User Permanently
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUser && selectedUser && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowEditUser(false)}></div>
              
              <div className="inline-block w-full max-w-4xl overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle">
                <form onSubmit={saveUserChanges}>
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Edit User: {selectedUser.name}</h3>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Column - Basic Info */}
                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-800 border-b pb-2">Basic Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                          <input
                            type="text"
                            value={editUserData.name || ''}
                            onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                            <span className="text-xs text-gray-500 ml-2">(Read-only - cannot be edited)</span>
                          </label>
                          <input
                            type="email"
                            value={editUserData.email || ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                            <span className="text-xs text-gray-500 ml-2">(Read-only - cannot be edited)</span>
                          </label>
                          <input
                            type="tel"
                            value={editUserData.phone || ''}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                          <input
                            type="date"
                            value={editUserData.date_of_birth || ''}
                            onChange={(e) => setEditUserData({...editUserData, date_of_birth: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                          <select
                            value={editUserData.gender || ''}
                            onChange={(e) => setEditUserData({...editUserData, gender: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">User Type</label>
                          <select
                            value={editUserData.user_type || ''}
                            onChange={(e) => setEditUserData({...editUserData, user_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="user">User</option>
                            <option value="doctor">Doctor</option>
                            <option value="provider">Provider</option>
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Right Column - Insurance & Profile */}
                      <div className="space-y-4">
                        <h4 className="text-md font-medium text-gray-800 border-b pb-2">Insurance Information</h4>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Company</label>
                          <input
                            type="text"
                            value={editUserData.insurance_company || ''}
                            onChange={(e) => setEditUserData({...editUserData, insurance_company: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., Gulf Insurance Company Kuwait"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Insurance Number</label>
                          <input
                            type="text"
                            value={editUserData.insurance_number || ''}
                            onChange={(e) => setEditUserData({...editUserData, insurance_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., GIC-KWT-2025-123456"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Policy Holder Name</label>
                          <input
                            type="text"
                            value={editUserData.policy_holder_name || ''}
                            onChange={(e) => setEditUserData({...editUserData, policy_holder_name: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Group Number</label>
                          <input
                            type="text"
                            value={editUserData.group_number || ''}
                            onChange={(e) => setEditUserData({...editUserData, group_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Profile Picture URL</label>
                          <input
                            type="url"
                            value={editUserData.picture || ''}
                            onChange={(e) => setEditUserData({...editUserData, picture: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="https://example.com/profile.jpg"
                          />
                        </div>
                        
                        {editUserData.picture && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Current Profile Picture</label>
                            <img 
                              src={editUserData.picture} 
                              alt="Profile Preview" 
                              className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Appointments Section */}
                    {userAppointments.length > 0 && (
                      <div className="mt-8">
                        <h4 className="text-md font-medium text-gray-800 border-b pb-2 mb-4">Recent Appointments ({userAppointments.length})</h4>
                        <div className="max-h-60 overflow-y-auto">
                          <div className="space-y-3">
                            {userAppointments.map((appointment) => (
                              <div key={appointment.id} className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {appointment.appointment_date} at {appointment.appointment_time}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      Status: <span className={`font-medium ${
                                        appointment.status === 'confirmed' ? 'text-green-600' :
                                        appointment.status === 'cancelled' ? 'text-red-600' :
                                        'text-yellow-600'
                                      }`}>{appointment.status}</span>
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                      ID: {appointment.id}
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    {appointment.status !== 'cancelled' && (
                                      <button
                                        onClick={() => handleAppointmentAction(appointment.id, 'cancel')}
                                        className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                                      >
                                        Cancel
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowEditUser(false)}
                      className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Doctor Verification Modal */}
        {showDoctorVerification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">Doctor Verification Management</h2>
                <button
                  onClick={() => setShowDoctorVerification(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 sm:p-4 lg:p-6">
                <div className="mb-3 sm:mb-4">
                  <button
                    onClick={fetchPendingDoctors}
                    className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm w-full sm:w-auto"
                  >
                    Refresh Pending Doctors
                  </button>
                </div>
                
                {pendingDoctors.length === 0 ? (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <p className="text-sm sm:text-base">No pending doctor verifications found.</p>
                    <p className="text-xs sm:text-sm mt-2">Click "Refresh Pending Doctors" to load latest data.</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                      Found {pendingDoctors.length} doctor(s) awaiting verification
                    </p>
                    
                    {pendingDoctors.map((doctor) => (
                      <VerificationReviewCard
                        key={doctor.id}
                        user={doctor}
                        adminToken={adminToken}
                        onApproveAll={(id) => verifyDoctor(id, true)}
                        onRejectAll={(id) => verifyDoctor(id, false)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Search Modal */}
        {showUserSearch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <h2 className="text-base sm:text-xl font-semibold text-gray-900">🔍 User Search & Management</h2>
                <button
                  onClick={() => setShowUserSearch(false)}
                  className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 sm:p-4 lg:p-6">
                {/* Search Bar */}
                <div className="mb-4 sm:mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={userSearchQuery}
                      onChange={handleSearchInputChange}
                      className="block w-full pl-8 sm:pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base lg:text-lg"
                      placeholder="Search by name, email, phone..."
                    />
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-2">
                    💡 Search across all user types - users, doctors, providers. Type at least 2 characters.
                  </p>
                </div>

                {/* Search Status */}
                {isSearching && (
                  <div className="text-center py-4">
                    <div className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-gray-600">Searching...</span>
                    </div>
                  </div>
                )}

                {/* Search Results */}
                {!isSearching && userSearchQuery.length >= 2 && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">
                      {searchResults.length > 0 ? 
                        `Found ${searchResults.length} user(s) matching "${userSearchQuery}"` : 
                        `No users found matching "${userSearchQuery}"`
                      }
                    </p>
                  </div>
                )}

                {/* User Cards */}
                {searchResults.length > 0 && (
                  <div className="space-y-4">
                    {searchResults.map((user) => (
                      <div key={user.id} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {/* User Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h3 className="text-lg font-bold text-gray-900">
                                  {user.user_type === 'provider' ? 
                                    (user.facility_name || 'Unnamed Facility') : 
                                    `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed User'
                                  }
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    user.user_type === 'doctor' ? 'bg-blue-100 text-blue-800' :
                                    user.user_type === 'provider' ? 'bg-purple-100 text-purple-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {user.user_type === 'patient' ? 'User' : 
                                     user.user_type?.charAt(0).toUpperCase() + user.user_type?.slice(1)}
                                  </span>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    user.is_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {user.is_verified ? '✅ Verified' : '⏳ Pending'}
                                  </span>
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    Active
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Contact Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div className="bg-blue-50 p-3 rounded-lg">
                                <h4 className="font-semibold text-blue-800 mb-2">📞 Contact Info</h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">Email:</span> <span className="text-blue-600">{user.email}</span></p>
                                  <p><span className="font-medium">Phone:</span> <span className="font-mono">{user.phone}</span></p>
                                  {user.date_of_birth && (
                                    <p><span className="font-medium">DOB:</span> {user.date_of_birth}</p>
                                  )}
                                  {user.gender && (
                                    <p><span className="font-medium">Gender:</span> {user.gender}</p>
                                  )}
                                </div>
                              </div>

                              <div className="bg-gray-50 p-3 rounded-lg">
                                <h4 className="font-semibold text-gray-800 mb-2">🔒 Account Details</h4>
                                <div className="space-y-1 text-sm">
                                  <p><span className="font-medium">User ID:</span> <span className="font-mono text-xs">{user.id}</span></p>
                                  <p><span className="font-medium">Created:</span> {new Date(user.created_at).toLocaleDateString()}</p>
                                  <p><span className="font-medium">Last Updated:</span> {new Date(user.updated_at).toLocaleDateString()}</p>
                                  {user.last_login && (
                                    <p><span className="font-medium">Last Login:</span> {new Date(user.last_login).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Professional Information (for doctors/providers) */}
                            {user.user_type === 'doctor' && (
                              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <h4 className="font-semibold text-blue-800 mb-2">👨‍⚕️ Doctor Information</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p><span className="font-medium">Specialization:</span> {user.specialization || 'Not specified'}</p>
                                    <p><span className="font-medium">Qualification:</span> {user.qualification || 'Not provided'}</p>
                                    <p><span className="font-medium">Experience:</span> {user.experience_years || 0} years</p>
                                  </div>
                                  <div>
                                    <p><span className="font-medium">License:</span> {user.license_number || 'Not provided'}</p>
                                    <p><span className="font-medium">Consultation Fee:</span> {user.consultation_fee || 'Not set'} KD</p>
                                  </div>
                                </div>
                                {user.bio && (
                                  <div className="mt-2">
                                    <p className="text-sm"><span className="font-medium">Bio:</span> {user.bio}</p>
                                  </div>
                                )}
                              </div>
                            )}

                            {user.user_type === 'provider' && (
                              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                <h4 className="font-semibold text-purple-800 mb-2">🏥 Provider Information</h4>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p><span className="font-medium">Facility:</span> {user.facility_name || 'Not provided'}</p>
                                    <p><span className="font-medium">Address:</span> {user.address || 'Not provided'}</p>
                                    <p><span className="font-medium">License:</span> {user.license_number || 'Not provided'}</p>
                                  </div>
                                  <div>
                                    {user.google_maps_link && (
                                      <p><span className="font-medium">Location:</span> 
                                        <a href={user.google_maps_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                                          📍 Maps
                                        </a>
                                      </p>
                                    )}
                                    {user.website && (
                                      <p><span className="font-medium">Website:</span> 
                                        <a href={user.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                                          🌐 Visit
                                        </a>
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="ml-4 flex flex-col space-y-2 min-w-[140px]">
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setEditUserData({
                                  first_name: user.first_name || '',
                                  last_name: user.last_name || '',
                                  email: user.email || '',
                                  phone: user.phone || '',
                                  age: user.age || '',
                                  gender: user.gender || '',
                                  user_type: user.user_type || 'user',
                                  insurance_company: user.insurance_company || '',
                                  insurance_number: user.insurance_number || '',
                                  insurance_type: user.insurance_type || '',
                                  coverage_type: user.coverage_type || '',
                                  policy_holder_name: user.policy_holder_name || ''
                                });
                                setShowUserSearch(false); // Close search modal
                              }}
                              className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                            >
                              ✏️ Edit
                            </button>
                            
                            <button
                              onClick={() => impersonateUser(user.id)}
                              className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                            >
                              👤 Login As
                            </button>
                            
                            {/* Activate/Deactivate feature removed - all users are active */}

                            {!user.is_verified && (
                              <button
                                onClick={() => {
                                  if (user.user_type === 'doctor') {
                                    verifyDoctor(user.id, true);
                                  } else if (user.user_type === 'provider') {
                                    verifyProvider(user.id, true);
                                  }
                                }}
                                className="bg-yellow-600 text-white px-3 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm font-medium"
                              >
                                ✅ Verify
                              </button>
                            )}

                            <button
                              onClick={() => {
                                const userInfo = `
                                  Name: ${user.user_type === 'provider' ? user.facility_name : `${user.first_name} ${user.last_name}`}
                                  Email: ${user.email}
                                  Phone: ${user.phone}
                                  Type: ${user.user_type}
                                  Status: Active
                                  Verified: ${user.is_verified ? 'Yes' : 'No'}
                                  Created: ${new Date(user.created_at).toLocaleDateString()}
                                `;
                                navigator.clipboard.writeText(userInfo);
                                alert('User information copied to clipboard');
                              }}
                              className="bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
                            >
                              📋 Copy
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No Results State */}
                {!isSearching && userSearchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No users match your search criteria. Try different keywords.
                    </p>
                  </div>
                )}

                {/* Initial State */}
                {userSearchQuery.length < 2 && !isSearching && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Start searching for users</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Type a name, email, phone number, or specialization to find users instantly.
                    </p>
                    <div className="mt-4 space-y-1 text-xs text-gray-400">
                      <p>💡 Search examples:</p>
                      <p>• "Dr. Ahmad" - Find doctor by name</p>
                      <p>• "cardiology" - Find by specialization</p>
                      <p>• "kuwait hospital" - Find by facility name</p>
                      <p>• "+965" - Find by phone number</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Provider Verification Modal */}
        {showProviderVerification && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Provider Verification Management</h2>
                <button
                  onClick={() => setShowProviderVerification(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6">
                <div className="mb-4">
                  <button
                    onClick={fetchPendingProviders}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Refresh Pending Providers
                  </button>
                </div>
                
                {pendingProviders.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No pending provider verifications found.</p>
                    <p className="text-sm mt-2">Click "Refresh Pending Providers" to load latest data.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Found {pendingProviders.length} provider(s) awaiting verification
                    </p>
                    
                    {pendingProviders.map((provider) => (
                      <div key={provider.id} className="space-y-2">
                        <VerificationReviewCard
                          user={provider}
                          adminToken={adminToken}
                          onApproveAll={(id) => verifyProvider(id, true)}
                          onRejectAll={(id) => verifyProvider(id, false)}
                        />
                        <button
                          onClick={() => { setPhotoApprovalUserId(provider.id); setShowPhotoApprovals(true); }}
                          data-testid={`provider-review-photos-btn-${provider.id}`}
                          className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                        >
                          <ImageIcon className="w-4 h-4" />
                          Review this provider's photos
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Google Analytics Dashboard Modal */}
        {showAnalytics && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <svg className="w-7 h-7 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Google Analytics Dashboard
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Track website traffic, user behavior, and conversions</p>
                </div>
                <button
                  onClick={() => setShowAnalytics(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Quick Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-5 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm font-medium">Total Users</p>
                        <p className="text-3xl font-bold mt-2">{stats.totalUsers}</p>
                      </div>
                      <svg className="w-12 h-12 text-blue-200 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-5 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm font-medium">Providers</p>
                        <p className="text-3xl font-bold mt-2">{stats.providers}</p>
                      </div>
                      <svg className="w-12 h-12 text-green-200 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-5 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm font-medium">Doctors</p>
                        <p className="text-3xl font-bold mt-2">{stats.doctors}</p>
                      </div>
                      <svg className="w-12 h-12 text-purple-200 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-5 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm font-medium">Patients</p>
                        <p className="text-3xl font-bold mt-2">{stats.patients}</p>
                      </div>
                      <svg className="w-12 h-12 text-orange-200 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Google Analytics Links */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 mb-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                    </svg>
                    Access Full Google Analytics Dashboard
                  </h3>
                  <p className="text-gray-600 mb-4">
                    For detailed insights, custom reports, and advanced analytics, access your Google Analytics dashboard directly.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <a
                      href="https://analytics.google.com/analytics/web/#/p448690059/reports/intelligenthome"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-white border-2 border-blue-500 text-blue-700 px-6 py-4 rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md group"
                    >
                      <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <div className="text-left">
                        <p className="font-semibold">Overview Dashboard</p>
                        <p className="text-sm text-gray-500">Real-time traffic & user metrics</p>
                      </div>
                      <svg className="w-5 h-5 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>

                    <a
                      href="https://analytics.google.com/analytics/web/#/p448690059/reports/explorer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 bg-white border-2 border-green-500 text-green-700 px-6 py-4 rounded-lg hover:bg-green-50 transition-all duration-200 shadow-sm hover:shadow-md group"
                    >
                      <svg className="w-6 h-6 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <div className="text-left">
                        <p className="font-semibold">Explore Reports</p>
                        <p className="text-sm text-gray-500">Custom queries & data exploration</p>
                      </div>
                      <svg className="w-5 h-5 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-1">Google Analytics is now tracking your website</p>
                      <p className="text-blue-700">
                        All page views, user interactions, and conversions are being automatically tracked. Visit the Google Analytics dashboard for detailed reports, audience insights, and conversion tracking. Data may take 24-48 hours to fully populate.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Review Management Modal */}
      {showReviewManager && <AdminReviewManager onClose={() => setShowReviewManager(false)} />}
      {showPhotoApprovals && (
        <AdminPhotoApprovals
          userId={photoApprovalUserId}
          onClose={() => { setShowPhotoApprovals(false); setPhotoApprovalUserId(null); fetchPendingPhotoCount(); }}
        />
      )}

      {/* Sponsored Provider Management Modal */}
      {showSponsoredManager && <AdminSponsoredManager onClose={() => setShowSponsoredManager(false)} />}

      {/* Promo Code Management Modal */}
      {showPromoManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="min-h-screen">
            <div className="relative">
              <button
                onClick={() => setShowPromoManager(false)}
                className="fixed top-4 right-4 z-[60] bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                data-testid="close-promo-manager-btn"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <PromoCodeManager />
            </div>
          </div>
        </div>
      )}

      {/* System Backup Management Modal */}
      {showBackupManager && <BackupManager onClose={() => setShowBackupManager(false)} />}

      <ContactChangeRequestsModal
        open={showContactChangeRequests}
        onClose={() => setShowContactChangeRequests(false)}
        adminToken={adminToken}
      />
    </div>
  );
};

export default AdminPanel;